import request from '@/utils/request';
import * as types from '@/api/repo/types';
import { reqCatch, reqCatchV2, type catchRt } from '@/utils/catch';
import type { AxiosResponse } from 'axios';
import { VITE_FILE_HOST, VITE_DOWNLOAD_HOST } from '@/utils/tools';
const fileUploadBaseURL = VITE_FILE_HOST();
const fileDownloadBaseURL = VITE_DOWNLOAD_HOST();
// 获取仓库文件
export function getRepoFiles(params: types.commonRepoReqType): Promise<AxiosResponse<types.commonRepoResType>> {
  return request({
    url: `/api/v2/projects/${params.repoId}/repository/files`,
    method: 'get',
    params
  });
}
// 编辑仓库文件
export function editRepoFiles(data: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${data.repoId}/repository/files`,
    method: 'put',
    data
  });
}
// 创建仓库文件
export function createRepoFiles(data: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${data.repoId}/repository/files`,
    method: 'post',
    data
  });
}
// 删除仓库文件(不能删除文件夹)
export function deleteRepoFiles(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${params.repoId}/repository/files`,
    method: 'delete',
    data: params
  });
}
// 批量删除目录和文件（支持文件夹删除）
export function batchDeleteRepoFiles(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${params.repoId}/repository/files_in_batch`,
    method: 'post',
    data: params
  }, {
    customTimeout: 60000
  });
}
// 获取文件追溯信息
export function getFileBlame(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${params.repoId}/repository/blame`,
    method: 'get',
    params
  });
}
// 批量删除指定项目仓库的目录和文件
export function batchDeleteFiles(data: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${data.repoId}/repository/files_in_batch`,
    method: 'post',
    data
  }, {
    customTimeout: 60000
  });
}
// 文件树查询(只能返回1层数组)
export function getRepoTree(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${params.repoId}/repository/tree`,
    method: 'get',
    params
  });
}
// 文件树查询(指定路径返回树形结构)
export function getRepoTreeByPath(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${params.repoId}/repository/upper_files_tree`,
    method: 'get',
    params
  });
}
// 获取仓库README文件
export function getRepoReadme(params: types.commonRepoReqType): catchRt<types.commonRepoResType> {
  return reqCatchV2(() =>
    request({
      url: `/api/v2/projects/${params.repoId}/repository/readme_file`,
      method: 'get',
      params
    })
  );
}

// 将 rst 文件内容转成 markdown 格式，传 base64，返回 base64
export function convertRst2Md(data: {
  project_id: string;
  rst_content_base64: string;
}): Promise<types.commonRepoResType> {
  const { project_id, rst_content_base64 } = data;
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${project_id}/rst_to_md`,
        method: 'post',
        data: { rst_content_base64 }
      }),
    data
  );
}

// 获取仓库网络信息
export function getRepoNetwork(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${params.repoId}/repository/network`,
    method: 'get',
    params
  });
}
// 日志树查询
export function getRepoLogsTree(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${params.repoId}/repository/logs_tree`,
    method: 'get',
    params
  });
}
// 获取默认分支
export function getRepoDefaultBranch(params: types.commonRepoReqType): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${params.repoId}/repository/default_branch`,
        method: 'get',
        params
      }),
    params
  );
}
// 获取分支概览的分支信息
export function getRepoBranchView(params: types.commonRepoReqType): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${params.repoId}/repository/branch_overview`,
        method: 'get',
        params
      }),
    params
  );
}
// 获取项目仓库比较信息
export function getRepoCompare(params: types.commonRepoReqType): Promise<any> {
  const { customTimeout, ...rest } = params;
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${rest.repoId}/repository/compare`,
        method: 'get',
        params: rest
      }, {
        customTimeout: customTimeout || 30000,
        customError: true,
        ignoreError: true
      }),
    params
  );
}
// 获取文件查看链接（浏览器打开）
export function getFileRaw(data: types.commonRepoReqType): Promise<string> {
  return Promise.resolve(
    `${fileDownloadBaseURL}/${decodeURIComponent(data.repoId)}/raw/${encodeURIComponent(data.ref)}/${data.filePath}`
  );
}
// 获取文件下载链接（附件下载）
export function getBlobRaw(params: types.commonRepoReqType): Promise<string> {
  return Promise.resolve(
    `${fileDownloadBaseURL}/${decodeURIComponent(params.repoId)}/blobs/${params.sha}/${params.file_name}`
  );
}

// 获取文件下载链接（附件下载 模型文件）
// 获取项目的模型分析和使用的模型列表
export function getRepoFileDownloadByAiBlobs(params: types.aiFileType) {
  return request(
    {
      url: `/api/v2/ai-hub/nauth/projects/${params.repoId}/repository/lfs/ai-blobs/${params.lfsOidSha256}/addr`,
      method: 'get'
    }
  );
}

// 获取所有的项目白名单
// 查询auth_namespace白名单时需要传auth_namespace true
export function getAllWl(params: types.commonRepoReqType): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/wl/${params.repoId}/all_wl`,
        method: 'get',
        params
      })
  );
}

// 仓库文件上传
export function uploadRepoFile(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  // 使用formData上传
  const formData = new FormData();
  Object.entries(params).forEach(([key, val]) => {
    formData.append(key, val);
  });

  return request(
    {
      url: `${fileUploadBaseURL}/${decodeURIComponent(params.repoId)}/uploads/file`,
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data; boundary=----WebKitFormBoundaryn8D9asOnAnEU4Js0'
      },
      withCredentials: true
    },
    {
      customError: true,
      customTimeout: 60 * 1000 * 10
    }
  );
}
// 仓库多文件上传
export function uploadMultipleRepoFile(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  // 使用formData上传
  const formData = new FormData();
  Object.entries(params).forEach(([key, val]) => {
    if (key === 'files') {
      for (const file of val) {
        formData.append(key, file);
      }
    } else {
      formData.append(key, val);
    }
  });
  return request(
    {
      url: `${fileUploadBaseURL}/${decodeURIComponent(params.repoId)}/uploads/files`,
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data; boundary=----WebKitFormBoundaryn8D9asOnAnEU4Js0'
      },
      withCredentials: true
    },
    {
      customError: true,
      customTimeout: 60 * 1000 * 10
    }
  );
}

// 仓库 issue，mr 图片上传方法
export function uploadRepoImage(data: {
  project_id: string;
  attach: string;
  file_name: string;
}): Promise<types.commonRepoResType> {
  const { project_id, attach, file_name } = data;
  return reqCatch(
    () =>
      request({
        url: `${fileUploadBaseURL}/${project_id}/uploads/image_file`,
        method: 'post',
        data: { attach, file_name }
      }),
    data
  );
}
// 获取仓库下载压缩包
export function getRepoArchive(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v2/projects/${params.repoId}/repository/archive`,
    method: 'get',
    params
  });
}

export function getRepoRecommend(params: types.commonRepoReqType): Promise<types.commonRepoResType> {
  return request({
    url: `/api/v1/agg/index`,
    method: 'get',
    params
  });
}
